<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

type Props = {
  threshold?: number | number[];
  rootMargin?: string;
  once?: boolean;
  appear?: boolean;
  wrapperTag?: string;
  contentTag?: string;
  contentClass?: string;
};

const props = withDefaults(defineProps<Props>(), {
  threshold: 0.1,
  rootMargin: '0px',
  once: true,
  appear: true,
  wrapperTag: 'div',
  contentTag: 'div',
  contentClass: '',
});

const sentinelRef = ref<HTMLElement | null>(null);
const isIntersecting = ref(false);
const hasEnteredOnce = ref(false);

const rendered = computed(() => {
  if (props.once) return hasEnteredOnce.value || isIntersecting.value;
  return isIntersecting.value;
});

let observer: IntersectionObserver | null = null;

onMounted(() => {
  if (!sentinelRef.value) return;

  observer = new IntersectionObserver(
    ([entry]) => {
      const now = !!entry?.isIntersecting;
      isIntersecting.value = now;

      if (now) {
        hasEnteredOnce.value = true;
        if (props.once && observer && sentinelRef.value) {
          observer.unobserve(sentinelRef.value);
        }
      }
    },
    { threshold: props.threshold, rootMargin: props.rootMargin },
  );

  observer.observe(sentinelRef.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});
</script>

<template>
  <component :is="wrapperTag">
    <!-- Always-present target for IntersectionObserver -->
    <span
      ref="sentinelRef"
      class="io-sentinel"
      aria-hidden="true"
    />

    <Transition
      name="scroll-fade"
      :appear="appear"
    >
      <component
        :is="contentTag"
        v-if="rendered"
        :class="contentClass"
      >
        <slot />
      </component>
    </Transition>
  </component>
</template>

<style scoped>
.io-sentinel {
  display: block;
  height: 1px;
  width: 1px;
}
</style>
